<template>
  <wizard-section
    :open="open"
    :show-summary="showSummary"
    class="accommodation-wizard"
    @apply="apply()"
    @cancel="cancel()"
    @open="doOpen()"
  >
    <template #summary>
      <accommodation-wizard-summary
        :category-ids="categoryIds"
        :is-search-results="isSearchResults"
      />
    </template>

    <template #body>
      <categories-selector
        :category-ids="categoryIdsModel"
        :category-facet-counts="showCounts ? counts : undefined"
        :allowed-category-ids="allowedCategoryIds"
        :is-search-form="true"
        :facets="searchWizardStore.wizardFilters.facets"
        @change-category-ids="changeCategoryIds($event)"
        @subcategory-group-change="subcategoryGroupChange($event)"
      />
    </template>
  </wizard-section>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { isSearchUrlRoute } from '../../../search/isSearchUrlComponent'
import type { SearchFilters } from '../../../search/schemas'

@Component({
  setup() {
    const pinia = usePinia()
    const searchWizardStore = useSearchWizardStore(pinia)
    const searchStore = useSearchStoreSetup(pinia)
    return {
      searchWizardStore,
      searchStore,
    }
  },
  emits: ['open', 'cancel', 'changeCategoryIds', 'close'],
})
class AccommodationWizard extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  searchStore: ReturnType<typeof useSearchStoreSetup>

  @Prop({ required: true, type: Array })
  categoryIds: string[]

  @Prop({ required: true, type: Object })
  searchFilters: SearchFilters

  @Prop({ required: true, type: Boolean })
  open: boolean

  @Prop({ required: true, type: Boolean })
  showSummary: boolean

  @Prop({ required: false, type: Boolean, default: true })
  enabled: boolean

  @Prop({ required: false, type: Boolean, default: false })
  isSearchResults: boolean

  @Prop({ required: false, type: Boolean, default: true })
  showCounts: boolean

  @Prop({ required: false })
  allowedCategoryIds?: string[]

  facets = {
    enabled: [] as string[],
    disabled: [] as string[],
  }

  get counts() {
    const searchStore = useSearchStoreSetup(usePinia())
    return {
      ...searchStore.searchCounts?.categoriesCounts,
      ...searchStore.searchCounts?.keywordsCounts,
    }
  }

  categoryIdsModel: string[] = []

  created() {
    this.populateModel()
  }

  async mounted() {
    if (!this.counts) {
      await this.searchWizardStore.updateCounts()
    }
  }

  @Watch('categoryIds')
  updateCategoryIdsModel() {
    this.populateModel()
  }

  async changeCategoryIds(newCategoryIds: string[]) {
    this.categoryIdsModel = [...newCategoryIds]

    // Update counts with temporary filters without changing the search or URL
    const tempFilters = {
      ...this.searchWizardStore.wizardFilters,
      categoryIds: newCategoryIds,
    }
    await this.searchStore.countsSearch(tempFilters)
  }

  doOpen() {
    if (this.enabled) {
      this.populateModel()
      this.$emit('open')
    }
  }

  populateModel() {
    this.categoryIdsModel = [...this.categoryIds]
  }

  apply() {
    // Always update categories
    void this.searchWizardStore.setCategoryIds(this.categoryIdsModel)

    // Always update facets if they have been changed
    if (this.facets.enabled.length > 0 || this.facets.disabled.length > 0) {
      void this.searchWizardStore.enableDisableFacets(
        this.facets.enabled,
        this.facets.disabled,
      )
    }

    this.$emit('changeCategoryIds', this.categoryIdsModel)
    this.$emit('close')
  }

  cancel() {
    this.resetModel()
    this.$emit('cancel')
  }

  resetModel() {
    this.categoryIdsModel = [...this.categoryIds]
    this.$emit('close')
  }

  subcategoryGroupChange(event: { enabled: string[]; disabled: string[] }) {
    this.facets = {
      enabled: event.enabled,
      disabled: event.disabled,
    }
    // Facets will be applied to store only when Apply button is pressed
    // This matches the pattern used for category changes
  }
}

export default toNative(AccommodationWizard)
</script>
